/**
 * Embed Feature Flags — Centralized control for what's visible in Fabric embed mode.
 *
 * SECURITY: Features are DISABLED by default in embed mode. New features added to
 * IntuigenceAI won't leak into Fabric unless explicitly allowlisted here.
 *
 * Usage in components:
 *   import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'
 *   if (isFeatureAllowedInEmbed('aiChat')) { ... }
 *
 * Or use the React hook:
 *   import { useEmbedFeature } from '@/lib/embedFeatures'
 *   const showChat = useEmbedFeature('aiChat')
 */

import { isFabricEmbedMode } from './fabricEmbedAuth'

/**
 * Exhaustive list of features that can be toggled in embed mode.
 * Add new features here as they are developed.
 */
export type EmbedFeature =
  | 'aiChat'              // AI chat panel + button
  | 'documentsModal'      // Add/Remove Files modal (triggered via PostMessage)
  | 'canvasEditing'       // tldraw canvas drawing/editing
  | 'documentPreview'     // PDF/document preview inside shapes
  | 'documentActions'     // Remove from board / View Document buttons on shapes
  | 'shareModal'          // Share board modal
  | 'chatShare'           // Share a chat conversation (FS-663)
  | 'connectorsModal'     // Connectors modal
  | 'commentMode'         // Comment pins and panel
  | 'boardToolbar'        // Board-level toolbar (file/connector buttons)
  | 'boardNavigation'     // Back button, board list navigation
  | 'meetingIntegration'  // Recall.ai meeting features
  | 'catalogScope'        // "Use Entire Data Catalog" — widens retrieval past the board

/**
 * Features allowed in embed mode. Everything NOT listed here is blocked.
 *
 * `catalogScope` is deliberately absent, and it is the one entry worth spelling out:
 * the host decides which documents an embedded board may see, and catalog scope is
 * precisely the control that steps outside that set — a catalog-scoped turn resolves
 * its roster from `resolveCatalogRoster(tenantId)`, the whole tenant, with no
 * host-granted intersection. Offering it inside an embed hands the embedded user a
 * button that reads documents the host never granted.
 *
 * `meetingIntegration` is allowed: Recall's bot joins Meet/Teams server-side, so the
 * mic/Permissions-Policy blocker that #27991 hit does not apply to it, and its API call
 * authenticates through the embed token like any other.
 */
const EMBED_ALLOWED_FEATURES: ReadonlySet<EmbedFeature> = new Set<EmbedFeature>([
  'aiChat',
  'documentsModal',
  'canvasEditing',
  'documentPreview',
  'boardToolbar',
  'meetingIntegration',
])

/**
 * Check if a feature should be shown in the current mode.
 * - Standalone app: always returns true (all features available)
 * - Embed mode: returns true only if the feature is in the allowlist
 */
export function isFeatureAllowedInEmbed(feature: EmbedFeature): boolean {
  if (!isFabricEmbedMode()) return true
  return EMBED_ALLOWED_FEATURES.has(feature)
}

/**
 * React-friendly hook version. Returns false for blocked features in embed mode.
 */
export function useEmbedFeature(feature: EmbedFeature): boolean {
  // isFabricEmbedMode() is a stable module-level flag set once at init,
  // so it's safe to call directly without useState/useEffect.
  return isFeatureAllowedInEmbed(feature)
}
